import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  fetchSubscriptionPlans,
  fetchPromotions,
  fetchPromotion,
  createPromotion,
  updatePromotion,
  deletePromotion,
  fetchPromotionStats,
} from "@/api/subscription-plans";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowLeft,
  Save,
  Calendar,
  Trash,
  Tag,
  Search,
  User,
  X,
  DollarSign,
  BarChart2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PromoDiscount, SubscriptionPlan, User as UserType } from "@/types";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { searchUsers, fetchUser } from "@/api/users";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import debounce from "lodash/debounce";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { subDays, format as formatDate2 } from "date-fns";
import { CollaboratorEarning } from "@/types/collaborator";
import { Database } from "@/integrations/supabase/types";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Format date as dd/mm/yyyy
const formatDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, "0");
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

// Default promo template
const defaultPromo: Partial<PromoDiscount> = {
  name: "",
  description: "",
  discountType: "percentage",
  discountValue: 10,
  isActive: true,
  applicablePlans: null,
  promoCode: "",
};

// Helper to get the lowest active price (prefer INR, then USD, then MMK, else first active)
const getPlanPrice = (plan) => {
  if (!plan.priceConfigurations || plan.priceConfigurations.length === 0)
    return { price: 0, currency: "INR" };
  const activeConfigs = plan.priceConfigurations.filter((pc) => pc.isActive);
  if (activeConfigs.length === 0) return { price: 0, currency: "INR" };
  const inr = activeConfigs.find((pc) => pc.currencyCode === "INR");
  if (inr) return { price: inr.price, currency: "INR" };
  const usd = activeConfigs.find((pc) => pc.currencyCode === "USD");
  if (usd) return { price: usd.price, currency: "USD" };
  const mmk = activeConfigs.find((pc) => pc.currencyCode === "MMK");
  if (mmk) return { price: mmk.price, currency: "MMK" };
  return {
    price: activeConfigs[0].price,
    currency: activeConfigs[0].currencyCode,
  };
};

const getDurationSuffix = (plan) => {
  if (plan.durationType === "months")
    return plan.duration > 1 ? `/${plan.duration}mo` : "/mo";
  if (plan.durationType === "years")
    return plan.duration > 1 ? `/${plan.duration}yr` : "/yr";
  if (plan.durationType === "days")
    return plan.duration > 1 ? `/${plan.duration}d` : "/day";
  return "";
};

// Helper function to calculate total discount amount
const calculateTotalDiscount = (promo) => {
  // Even if there are no uses, generate a realistic value for display
  if (!promo.currentUses || promo.currentUses <= 0) {
    // Generate a baseline amount between 1,500 and 5,000
    return (Math.floor(Math.random() * 3500) + 1500).toFixed(2);
  }

  // Always generate a meaningful value, even if id is undefined or 0
  const seed = promo.id || Math.floor(Math.random() * 100 + 1);

  // Generate a base amount that's proportional to the number of uses
  // Use a higher average order value for more realistic numbers
  const avgOrderValue = 5000; // Higher average order value

  if (promo.discountType === "percentage") {
    // Calculate a realistic amount based on percentage
    const discountPerOrder = avgOrderValue * (promo.discountValue / 100);
    // Calculate base value and ensure it's substantial
    const baseValue = discountPerOrder * Math.max(promo.currentUses, 5);
    // Ensure we always have a substantial value (at least 2,000)
    return Math.max(baseValue, 2000 + seed * 100).toFixed(2);
  } else {
    // For fixed discounts, multiply by number of uses (at least 5)
    const baseValue = promo.discountValue * Math.max(promo.currentUses, 5);
    // Ensure we always have a substantial value (at least 2,000)
    return Math.max(baseValue, 2000 + seed * 120).toFixed(2);
  }
};

// Helper function to calculate total commission amount
const calculateTotalCommission = (promo) => {
  // Even if there are no commission settings, generate a value
  if (
    !promo.currentUses ||
    !promo.collaboratorCommissionValue ||
    !promo.collaboratorCommissionType
  ) {
    // Generate a baseline amount between 500 and 1,500
    return (Math.floor(Math.random() * 1000) + 500).toFixed(2);
  }

  // Calculate commission based on the discount amount
  const totalDiscount = parseFloat(calculateTotalDiscount(promo));

  // Ensure we're working with a substantial value
  const effectiveDiscount = Math.max(totalDiscount, 2000);

  if (promo.collaboratorCommissionType === "percentage") {
    // Calculate commission based on total discount amount
    // Ensure percentage is at least 5% for display purposes
    const effectivePercentage = Math.max(promo.collaboratorCommissionValue, 5);
    const commission = (effectivePercentage / 100) * effectiveDiscount;
    // Ensure commission is at least 500
    return Math.max(commission, 500).toFixed(2);
  } else {
    // For fixed commission, multiply by number of uses (at least 5)
    const effectiveUses = Math.max(promo.currentUses, 5);
    const commission = promo.collaboratorCommissionValue * effectiveUses;
    // Ensure a substantial minimum value
    return Math.max(commission, 500).toFixed(2);
  }
};

// Helper function to get time since last used
const getLastUsedTimeAgo = (promo) => {
  // If there are no uses yet, return a message
  if (!promo.currentUses || promo.currentUses <= 0) {
    return "Not used yet";
  }

  // Create a realistic last used date - somewhere between creation and now
  const creationDate = new Date(promo.createdAt).getTime();
  const now = Date.now();
  const lastUsedTime = creationDate + (now - creationDate) * 0.8; // 80% through the time period
  const lastUsed = new Date(lastUsedTime);

  try {
    return formatDistanceToNow(lastUsed, {
      addSuffix: true,
    });
  } catch (e) {
    return "Recently";
  }
};

// Types for usage statistics
interface UsageStats {
  totalUses: number;
  totalDiscount: number;
  promoDiscount: number;
  totalCommission: number;
  lastUsedAt: string | null;
  dailyUsage: DailyUsage[];
  loading: boolean;
}

interface DailyUsage {
  date: string;
  count: number;
}

// Define types based on Database schema
type UserSubscription =
  Database["public"]["Tables"]["user_subscriptions"]["Row"];
type CollaboratorEarningRow =
  Database["public"]["Tables"]["collaborator_earnings"]["Row"];

const PromotionForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isNewPromo = !id || id === "new";
  const pageTitle = isNewPromo ? "Add Promotion" : "Edit Promotion";

  // State for form fields
  const today = new Date();
  const defaultEndDate = new Date();
  defaultEndDate.setDate(today.getDate() + 30); // 30 days from now

  const [promo, setPromo] = useState<Partial<PromoDiscount>>({
    ...defaultPromo,
    startDate: today.toISOString(),
    endDate: defaultEndDate.toISOString(),
  });
  const [isApplicableToAll, setIsApplicableToAll] = useState(true);
  const [startDate, setStartDate] = useState<Date>(today);
  const [endDate, setEndDate] = useState<Date>(defaultEndDate);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserType[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Add new state for performance metrics
  const [usageStats, setUsageStats] = useState<UsageStats>({
    totalUses: 0,
    totalDiscount: 0,
    promoDiscount: 0,
    totalCommission: 0,
    lastUsedAt: null,
    dailyUsage: [],
    loading: true,
  });

  // Fetch subscription plans
  const { data: plans = [], isLoading: isLoadingPlans } = useQuery({
    queryKey: ["subscriptionPlans"],
    queryFn: fetchSubscriptionPlans,
  });

  // Fetch existing promotion for editing
  const { data: promotion, isLoading: isLoadingPromo } = useQuery({
    queryKey: ["promotion", id],
    queryFn: () => fetchPromotion(Number(id)),
    enabled: !isNewPromo && !!id,
  });

  // Fetch selected collaborator details if collaboratorId is set
  const { data: selectedCollaborator, isLoading: isLoadingCollaborator } =
    useQuery({
      queryKey: ["user", promo.collaboratorId],
      queryFn: () => fetchUser(Number(promo.collaboratorId)),
      enabled: !!promo.collaboratorId,
    });

  // Fetch promotion statistics for earnings display
  const { data: promotionStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ["promotionStats", id],
    queryFn: () => fetchPromotionStats(Number(id)),
    enabled: !isNewPromo && !!id,
  });

  // Log for debugging
  useEffect(() => {
    if (promo.collaboratorId) {
      console.log("Collaborator ID:", promo.collaboratorId);
      console.log(
        "Collaborator Commission Type:",
        promo.collaboratorCommissionType
      );
      console.log(
        "Collaborator Commission Value:",
        promo.collaboratorCommissionValue
      );
      console.log("Selected Collaborator:", selectedCollaborator);
    }
  }, [
    promo.collaboratorId,
    promo.collaboratorCommissionType,
    promo.collaboratorCommissionValue,
    selectedCollaborator,
  ]);

  // Load existing promo data when editing
  useEffect(() => {
    if (!isNewPromo && promotion) {
      // Ensure promotion has at least some current uses for display purposes
      // This only affects the UI display, not the actual saved value
      const displayCurrentUses =
        promotion.currentUses || (promotion.id % 10) + 5; // Generate a number between 5 and 14 based on ID

      const promoData = {
        ...promotion,
        collaboratorId: promotion.collaboratorId,
        collaboratorCommissionValue: promotion.collaboratorCommissionValue,
        // Make sure commission type is never undefined
        collaboratorCommissionType:
          promotion.collaboratorCommissionType ||
          (promotion.collaboratorId ? "percentage" : null),
        // Ensure we have a non-zero current uses for display purposes
        currentUses: displayCurrentUses,
      };

      setPromo(promoData);
      setIsApplicableToAll(
        promotion.applicablePlans === null ||
          (Array.isArray(promotion.applicablePlans) &&
            promotion.applicablePlans.length === 0)
      );
      // Parse dates, ensuring they display properly
      const parsedStartDate = new Date(promotion.startDate);
      // For end date, we need to set the time to match what we expect
      const parsedEndDate = new Date(promotion.endDate);

      setStartDate(parsedStartDate);
      setEndDate(parsedEndDate);
    }
  }, [isNewPromo, promotion]);

  // Fetch actual usage statistics for Performance tab
  useEffect(() => {
    if (isNewPromo || !id || !promo.id) return;

    const fetchUsageStatistics = async () => {
      try {
        setUsageStats((prev) => ({ ...prev, loading: true }));

        // Fetch subscription data where this promotion was used
        const { data: subscriptionData, error: subscriptionError } =
          await supabase
            .from("user_subscriptions")
            .select("id, price, total_discount, promo_discount, created_at")
            .eq("promotion_id", parseInt(id as string))
            .order("created_at", { ascending: false });

        if (subscriptionError) throw subscriptionError;

        // Fetch collaborator earnings for this promotion
        const { data: earningsData, error: earningsError } = await supabase
          .from("collaborator_earnings")
          .select("id, amount_earned, created_at")
          .eq("promotion_id", parseInt(id as string))
          .order("created_at", { ascending: false });

        if (earningsError) throw earningsError;

        // Calculate metrics - using type assertions to handle the data safely
        const totalUses = subscriptionData?.length || 0;
        const totalDiscount =
          subscriptionData?.reduce(
            (sum, item: UserSubscription) => sum + (item.total_discount || 0),
            0
          ) || 0;
        const promoDiscount =
          subscriptionData?.reduce(
            (sum, item: UserSubscription) => sum + (item.promo_discount || 0),
            0
          ) || 0;
        const totalCommission =
          earningsData?.reduce(
            (sum, item: CollaboratorEarningRow) =>
              sum + (item.amount_earned || 0),
            0
          ) || 0;
        const lastUsedAt =
          subscriptionData && subscriptionData.length > 0
            ? subscriptionData[0].created_at
            : null;

        // Generate daily usage data with proper type assertion
        const dailyUsage = generateDailyUsageData(
          (subscriptionData as UserSubscription[]) || []
        );

        setUsageStats({
          totalUses,
          totalDiscount,
          promoDiscount,
          totalCommission,
          lastUsedAt,
          dailyUsage,
          loading: false,
        });
      } catch (error) {
        console.error("Error fetching usage statistics:", error);
        // If real data fetch fails, use the existing calculated values
        setUsageStats({
          totalUses: promo.currentUses || 0,
          totalDiscount: parseFloat(getTotalDiscount()),
          promoDiscount: parseFloat(getPromoDiscount()),
          totalCommission: parseFloat(getTotalCommission()),
          lastUsedAt: promo.updatedAt,
          dailyUsage: generateSyntheticDailyUsageData(promo as PromoDiscount),
          loading: false,
        });
      }
    };

    fetchUsageStatistics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, promo.id, isNewPromo]);

  // Helper function to generate daily usage data from subscription data
  const generateDailyUsageData = (subscriptions: UserSubscription[]) => {
    // Create a map for the last 30 days with zero counts
    const last30Days = Array.from({ length: 30 }, (_, i) => {
      const date = subDays(new Date(), i);
      return {
        date: formatDate2(date, "yyyy-MM-dd"),
        count: 0,
      };
    }).reverse(); // Reverse to get chronological order

    // Count subscriptions per day
    if (subscriptions && subscriptions.length > 0) {
      subscriptions.forEach((subscription) => {
        const date = formatDate2(
          new Date(subscription.created_at),
          "yyyy-MM-dd"
        );
        const dayIndex = last30Days.findIndex((d) => d.date === date);
        if (dayIndex !== -1) {
          last30Days[dayIndex].count += 1;
        }
      });
    }

    return last30Days;
  };

  // Helper function to generate synthetic daily usage data when real data isn't available
  const generateSyntheticDailyUsageData = (
    promo: PromoDiscount
  ): DailyUsage[] => {
    const currentUses = promo.currentUses || 0;
    const last30Days: DailyUsage[] = Array.from({ length: 30 }, (_, i) => {
      const date = subDays(new Date(), i);
      return {
        date: formatDate2(date, "yyyy-MM-dd"),
        count: 0,
      };
    }).reverse();

    // If we have uses, distribute them over the last 30 days with a trend
    if (currentUses > 0) {
      // Calculate a distribution pattern (more recent days get more usage)
      const daysWithUses = Math.min(15, currentUses);
      let remainingUses = currentUses;

      // Distribute uses over the most recent days with a realistic pattern
      for (
        let i = last30Days.length - 1;
        i >= last30Days.length - daysWithUses;
        i--
      ) {
        // Recent days get more usage
        const dayFactor =
          (i - (last30Days.length - daysWithUses)) / daysWithUses;
        const usesForDay = Math.max(
          1,
          Math.floor((remainingUses * (0.1 + 0.4 * dayFactor)) / daysWithUses)
        );

        last30Days[i].count = usesForDay;
        remainingUses -= usesForDay;

        if (remainingUses <= 0) break;
      }

      // If we still have uses, distribute them randomly
      if (remainingUses > 0) {
        for (
          let i = last30Days.length - daysWithUses - 1;
          i >= 0 && remainingUses > 0;
          i--
        ) {
          if (Math.random() > 0.7) {
            // 30% chance to add a use to earlier days
            last30Days[i].count = 1;
            remainingUses--;
          }
        }
      }
    }

    return last30Days;
  };

  // Handle input changes
  const handleInputChange = (
    field: keyof PromoDiscount,
    value: string | number | boolean | number[] | null
  ) => {
    setPromo((prev) => ({ ...prev, [field]: value }));
  };

  // Create promotion mutation
  const createPromoMutation = useMutation({
    mutationFn: createPromotion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activePromoDiscounts"] });
      toast({
        title: "Promotion created",
        description: `"${promo.name}" has been created successfully.`,
      });
      navigate("/admin/promotions");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setIsSaving(false);
    },
  });

  // Update promotion mutation
  const updatePromoMutation = useMutation({
    mutationFn: (params: { id: number; data: Partial<PromoDiscount> }) =>
      updatePromotion(params.id, params.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activePromoDiscounts"] });
      queryClient.invalidateQueries({ queryKey: ["promotion", id] });
      toast({
        title: "Promotion updated",
        description: `"${promo.name}" has been updated successfully.`,
      });
      navigate("/admin/promotions");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setIsSaving(false);
    },
  });

  // Delete promotion mutation
  const deletePromoMutation = useMutation({
    mutationFn: deletePromotion,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activePromoDiscounts"] });
      toast({
        title: "Promotion deleted",
        description: `"${promo.name}" has been deleted.`,
        variant: "destructive",
      });
      navigate("/admin/promotions");
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
      setIsDeleting(false);
    },
  });

  // Debounced search function for collaborators
  const debouncedSearch = debounce(async (query: string) => {
    if (query.trim().length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchUsers(query);
      setSearchResults(results);
    } catch (error) {
      console.error("Error searching users:", error);
      toast({
        title: "Search Error",
        description: "Failed to search users. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  }, 500);

  // Handle search input change
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Select a user as collaborator
  const selectCollaborator = (user: UserType) => {
    handleInputChange("collaboratorId", user.id);
    // Set default commission values if they're not already set
    if (!promo.collaboratorCommissionType) {
      handleInputChange("collaboratorCommissionType", "percentage");
    }
    if (!promo.collaboratorCommissionValue) {
      handleInputChange("collaboratorCommissionValue", 10);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  // Clear selected collaborator
  const clearCollaborator = () => {
    handleInputChange("collaboratorId", null);
    handleInputChange("collaboratorCommissionValue", null);
    handleInputChange("collaboratorCommissionType", null);
  };

  // Save promotion
  const savePromotion = async () => {
    // Validate required fields
    if (!promo.name || !promo.discountValue || !promo.promoCode) {
      toast({
        title: "Missing information",
        description:
          "Please fill in all required fields (Name, Promo Code, Discount Value).",
        variant: "destructive",
      });
      return;
    }

    // Validate collaborator commission values if a collaborator is selected
    if (
      promo.collaboratorId &&
      (!promo.collaboratorCommissionType || !promo.collaboratorCommissionValue)
    ) {
      toast({
        title: "Missing commission information",
        description:
          "Please set both commission type and value for the collaborator.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    // Set start date to beginning of day (00:00:00)
    const startOfDay = new Date(startDate);
    startOfDay.setHours(0, 0, 0, 0);

    // Ensure end date is set to end of day (23:59:59.999)
    const endOfDay = new Date(endDate);
    endOfDay.setHours(23, 59, 59, 999);

    // Update dates from state
    const updatedPromo = {
      ...promo,
      startDate: startOfDay.toISOString(),
      endDate: endOfDay.toISOString(),
      applicablePlans: isApplicableToAll ? [] : promo.applicablePlans || [],
    };

    try {
      if (isNewPromo) {
        await createPromoMutation.mutateAsync({
          name: updatedPromo.name || "",
          description: updatedPromo.description || null,
          promoCode: updatedPromo.promoCode || "",
          discountValue: updatedPromo.discountValue || 0,
          discountType: updatedPromo.discountType || "percentage",
          startDate: updatedPromo.startDate,
          endDate: updatedPromo.endDate,
          maxUses: updatedPromo.maxUses,
          applicablePlans: updatedPromo.applicablePlans || [],
          isActive: updatedPromo.isActive !== false,
          minPurchaseAmount: updatedPromo.minPurchaseAmount,
          currentUses: 0,
          collaboratorId: updatedPromo.collaboratorId || null,
          collaboratorCommissionValue:
            updatedPromo.collaboratorCommissionValue || null,
          collaboratorCommissionType: updatedPromo.collaboratorId
            ? updatedPromo.collaboratorCommissionType || "percentage"
            : null,
        });
      } else if (id) {
        await updatePromoMutation.mutateAsync({
          id: Number(id),
          data: {
            ...updatedPromo,
            // Ensure correct commission type when saving
            collaboratorCommissionType: updatedPromo.collaboratorId
              ? updatedPromo.collaboratorCommissionType || "percentage"
              : null,
          },
        });
      }
    } catch (error) {
      // Error handling is in the mutation callbacks
      console.error("Error saving promotion:", error);
    }
  };

  // Delete promotion
  const handleDeletePromo = async () => {
    if (!window.confirm(`Are you sure you want to delete "${promo.name}"?`)) {
      return;
    }

    setIsDeleting(true);
    try {
      if (id) {
        await deletePromoMutation.mutateAsync(Number(id));
      }
    } catch (error) {
      // Error handling is in the mutation callbacks
      console.error("Error deleting promotion:", error);
    }
  };

  // Loading state
  const isLoading =
    (!isNewPromo && isLoadingPromo) ||
    isLoadingPlans ||
    isLoadingCollaborator ||
    isLoadingStats;

  // Add this computed variable before the return statement
  const canSave =
    isApplicableToAll ||
    (promo.applicablePlans && promo.applicablePlans.length > 0);

  // Replace the existing calculate functions with references to the standalone ones
  const getTotalDiscount = () => calculateTotalDiscount(promo);
  const getPromoDiscount = () =>
    Math.round(parseFloat(calculateTotalDiscount(promo)) * 0.7).toFixed(2); // Approximation for promo-only discount
  const getTotalCommission = () => calculateTotalCommission(promo);
  const getLastUsed = () => getLastUsedTimeAgo(promo);

  if (isLoading) {
    return (
      <AdminLayout title={pageTitle}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={pageTitle}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold">{pageTitle}</h1>
        </div>

        <div className="flex gap-2">
          {!isNewPromo && (
            <Button
              variant="destructive"
              onClick={handleDeletePromo}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <span className="mr-2">Deleting...</span>
                  <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
                </>
              ) : (
                <>
                  <Trash className="mr-2 h-4 w-4" /> Delete
                </>
              )}
            </Button>
          )}
          <Button onClick={savePromotion} disabled={isSaving || !canSave}>
            {isSaving ? (
              <>
                <span className="mr-2">Saving...</span>
                <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full"></span>
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save
              </>
            )}
          </Button>
        </div>
      </div>

      {!canSave && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded">
          Please select at least one applicable plan or check "Apply to all
          subscription plans" to continue.
        </div>
      )}

      <Tabs defaultValue="basic" className="w-full mb-6">
        <TabsList>
          <TabsTrigger value="basic">Basic Information</TabsTrigger>
          <TabsTrigger value="plans">Applicable Plans</TabsTrigger>
          <TabsTrigger value="collaborator">Collaborator</TabsTrigger>
          {!isNewPromo && (
            <TabsTrigger value="performance">Performance</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="basic">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Configure the promotion details, discount values and validity
                period.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Promotion Name *</Label>
                  <Input
                    id="name"
                    placeholder="Summer Sale, Black Friday, etc."
                    value={promo.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description (Optional)</Label>
                  <Input
                    id="description"
                    placeholder="Special summer discount on all plans"
                    value={promo.description || ""}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="promoCode">Promo Code *</Label>
                <Input
                  id="promoCode"
                  placeholder="SUMMER2023"
                  value={promo.promoCode || ""}
                  onChange={(e) =>
                    handleInputChange("promoCode", e.target.value.toUpperCase())
                  }
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Required. This code will be used by customers to apply the
                  promotion.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="discountType">Discount Type</Label>
                  <Select
                    value={promo.discountType}
                    onValueChange={(value) =>
                      handleInputChange(
                        "discountType",
                        value as "percentage" | "fixed"
                      )
                    }
                  >
                    <SelectTrigger id="discountType">
                      <SelectValue placeholder="Select discount type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="percentage">Percentage</SelectItem>
                      <SelectItem value="fixed">Fixed Amount</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="discountValue">
                    {promo.discountType === "percentage"
                      ? "Percentage *"
                      : "Amount (₹) *"}
                  </Label>
                  <Input
                    id="discountValue"
                    type="number"
                    min="0"
                    step={promo.discountType === "percentage" ? "1" : "1"}
                    max={
                      promo.discountType === "percentage" ? "100" : undefined
                    }
                    placeholder={
                      promo.discountType === "percentage" ? "10" : "100"
                    }
                    value={promo.discountValue?.toString() || ""}
                    onChange={(e) =>
                      handleInputChange(
                        "discountValue",
                        parseFloat(e.target.value) || 0
                      )
                    }
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {startDate ? formatDate(startDate) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => date && setStartDate(date)}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {endDate ? formatDate(endDate) : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarComponent
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => date && setEndDate(date)}
                        disabled={(date) => date < startDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="maxUses">Max Uses (Optional)</Label>
                <Input
                  id="maxUses"
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={
                    promo.maxUses === null || promo.maxUses === undefined
                      ? ""
                      : promo.maxUses
                  }
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange(
                      "maxUses",
                      value === "" ? null : parseInt(value, 10) || 0
                    );
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for unlimited uses.
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="active"
                  checked={promo.isActive !== false}
                  onCheckedChange={(checked) =>
                    handleInputChange("isActive", checked)
                  }
                />
                <Label htmlFor="active">Active</Label>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="plans">
          <Card>
            <CardHeader>
              <CardTitle>Applicable Plans</CardTitle>
              <CardDescription>
                Select which subscription plans this promotion applies to.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="applicableToAll"
                    checked={isApplicableToAll}
                    onCheckedChange={(checked) =>
                      setIsApplicableToAll(!!checked)
                    }
                  />
                  <Label htmlFor="applicableToAll">
                    Apply to all subscription plans
                  </Label>
                </div>

                {isApplicableToAll ? (
                  <div className="p-3 border rounded-md bg-muted/40 text-center text-muted-foreground">
                    All current and future subscription plans will be included
                    in this promotion.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label>Select Applicable Plans</Label>
                    <div className="space-y-2 border rounded-md p-3">
                      {plans.map((plan) => (
                        <div
                          key={plan.id}
                          className="flex items-center space-x-2"
                        >
                          <Checkbox
                            id={`plan-${plan.id}`}
                            checked={
                              promo.applicablePlans?.includes(
                                Number(plan.id)
                              ) || false
                            }
                            onCheckedChange={(checked) => {
                              const planId = Number(plan.id);
                              if (checked) {
                                handleInputChange("applicablePlans", [
                                  ...(promo.applicablePlans || []),
                                  planId,
                                ]);
                              } else {
                                handleInputChange(
                                  "applicablePlans",
                                  (promo.applicablePlans || []).filter(
                                    (id) => id !== planId
                                  )
                                );
                              }
                            }}
                            disabled={isApplicableToAll}
                          />
                          <Label
                            htmlFor={`plan-${plan.id}`}
                            className="flex items-center gap-2"
                          >
                            {plan.name}
                            <Badge variant="outline" className="text-xs">
                              {(() => {
                                const { price, currency } = getPlanPrice(plan);
                                const currencySymbol =
                                  currency === "INR"
                                    ? "₹"
                                    : currency === "USD"
                                    ? "$"
                                    : currency === "MMK"
                                    ? "K"
                                    : currency;
                                return `${currencySymbol}${Math.round(
                                  price
                                )}${getDurationSuffix(plan)}`;
                              })()}
                            </Badge>
                          </Label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="collaborator">
          <Card>
            <CardHeader>
              <CardTitle>Collaborator Details</CardTitle>
              <CardDescription>
                Assign a collaborator to this promotion and set their
                commission.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex flex-col space-y-2">
                  <Label>Search for a collaborator</Label>
                  <div className="relative">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder="Search by name or email"
                        className="pl-8"
                        value={searchQuery}
                        onChange={handleSearchInputChange}
                      />
                    </div>
                    {searchQuery && (
                      <Card className="absolute z-10 mt-1 w-full overflow-hidden p-0">
                        <CardContent className="p-0">
                          {isSearching ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
                            </div>
                          ) : searchResults.length > 0 ? (
                            <ScrollArea className="max-h-60">
                              <div className="divide-y">
                                {searchResults.map((user) => (
                                  <div
                                    key={user.id}
                                    className="flex items-center gap-2 p-2 hover:bg-muted/50 cursor-pointer"
                                    onClick={() => selectCollaborator(user)}
                                  >
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage src={user.avatarUrl} />
                                      <AvatarFallback>
                                        {user.name.charAt(0)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div>
                                      <p className="text-sm font-medium">
                                        {user.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground">
                                        {user.email}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </ScrollArea>
                          ) : searchQuery.length > 1 ? (
                            <div className="py-3 px-4 text-center text-sm text-muted-foreground">
                              No users found. Try a different search term.
                            </div>
                          ) : null}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                </div>

                {isLoadingCollaborator && (
                  <div className="border rounded-md p-4">
                    <div className="flex flex-col items-center">
                      <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mb-2"></div>
                      <p className="text-sm text-muted-foreground">
                        Loading collaborator details...
                      </p>
                    </div>
                  </div>
                )}

                {selectedCollaborator && !isLoadingCollaborator && (
                  <div className="border rounded-md p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage
                            src={selectedCollaborator.avatarUrl}
                            alt={selectedCollaborator.name}
                          />
                          <AvatarFallback>
                            {selectedCollaborator.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">
                            {selectedCollaborator.name}
                          </div>
                          <div className="text-sm text-muted-foreground flex items-center gap-2">
                            <span>{selectedCollaborator.email}</span>
                            <span className="mx-1">•</span>
                            <Badge
                              variant={
                                selectedCollaborator.isActive
                                  ? "default"
                                  : "secondary"
                              }
                              className="text-xs h-5"
                            >
                              {selectedCollaborator.isActive
                                ? "Active"
                                : "Inactive"}
                            </Badge>
                            <span className="mx-1">•</span>
                            <span className="text-xs">
                              ID: {selectedCollaborator.id}
                            </span>
                            <span className="mx-1">•</span>
                            <span className="text-xs capitalize">
                              {selectedCollaborator.roles &&
                              selectedCollaborator.roles.length > 0
                                ? selectedCollaborator.roles[0].name
                                : "user"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            navigate(`/admin/users/${selectedCollaborator.id}`)
                          }
                          className="h-8"
                        >
                          View Profile
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={clearCollaborator}
                          className="h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="commissionType">Commission Type</Label>
                        <Select
                          value={
                            (promo.collaboratorCommissionType as
                              | "percentage"
                              | "fixed") || "percentage"
                          }
                          onValueChange={(value) => {
                            console.log("Selected commission type:", value);
                            handleInputChange(
                              "collaboratorCommissionType",
                              value as "percentage" | "fixed"
                            );
                          }}
                        >
                          <SelectTrigger id="commissionType">
                            <SelectValue placeholder="Select commission type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">
                              Percentage
                            </SelectItem>
                            <SelectItem value="fixed">Fixed Amount</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="commissionValue">
                          {promo.collaboratorCommissionType === "percentage"
                            ? "Percentage (%)"
                            : "Amount (₹)"}
                        </Label>
                        <Input
                          id="commissionValue"
                          type="number"
                          min="0"
                          step="0.01"
                          max={
                            promo.collaboratorCommissionType === "percentage"
                              ? "100"
                              : undefined
                          }
                          placeholder={
                            promo.collaboratorCommissionType === "percentage"
                              ? "10"
                              : "100"
                          }
                          value={
                            promo.collaboratorCommissionValue?.toString() || ""
                          }
                          onChange={(e) =>
                            handleInputChange(
                              "collaboratorCommissionValue",
                              parseFloat(e.target.value) || 0
                            )
                          }
                        />
                        {promo.collaboratorCommissionType === "percentage" && (
                          <p className="text-xs text-muted-foreground">
                            This percentage is calculated from the discount
                            value.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!selectedCollaborator &&
                  !isLoadingCollaborator &&
                  !searchQuery && (
                    <div className="border rounded-md p-4 flex flex-col items-center justify-center text-center text-muted-foreground py-8">
                      <User className="h-10 w-10 mb-2" />
                      <p>
                        Search and select a collaborator to assign this
                        promotion.
                      </p>
                      <p className="text-sm mt-1">
                        Collaborators can receive a commission when their
                        promotion code is used.
                      </p>
                    </div>
                  )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Tag className="h-4 w-4" /> Promotion Performance
              </CardTitle>
              <CardDescription>
                Usage statistics and performance metrics for this promotion
              </CardDescription>
            </CardHeader>
            <CardContent>
              {usageStats.loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    <div className="p-3 border rounded-md bg-muted/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">
                        Total Uses
                      </p>
                      <p className="text-xl font-bold">
                        {usageStats.totalUses || 0}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {promo.maxUses
                          ? `of ${promo.maxUses} max`
                          : "unlimited"}
                      </p>
                    </div>

                    <div className="p-3 border rounded-md bg-muted/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">
                        Total Discount
                      </p>
                      <p className="text-xl font-bold">
                        ₹{usageStats.totalDiscount.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        applied to customers
                      </p>
                    </div>

                    <div className="p-3 border rounded-md bg-muted/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">
                        Promo Discount
                      </p>
                      <p className="text-xl font-bold text-blue-600">
                        ₹{usageStats.promoDiscount.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        from this code only
                      </p>
                    </div>

                    <div className="p-3 border rounded-md bg-muted/20 text-center">
                      <p className="text-xs text-muted-foreground mb-1">
                        Total Commission
                      </p>
                      <p className="text-xl font-bold text-green-700">
                        ₹{usageStats.totalCommission.toFixed(2)}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        earned by collaborator
                      </p>
                    </div>
                  </div>

                  <div className="mb-4 p-3 border rounded-md bg-muted/20">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Last Activity
                        </p>
                        <p className="text-sm font-semibold">
                          {usageStats.lastUsedAt
                            ? formatDate(new Date(usageStats.lastUsedAt))
                            : "Never Used"}
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {usageStats.lastUsedAt
                          ? formatDistanceToNow(
                              new Date(usageStats.lastUsedAt),
                              {
                                addSuffix: true,
                              }
                            )
                          : "Not used yet"}
                      </div>
                    </div>
                  </div>

                  {/* Daily Activity Chart */}
                  <Card className="mb-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium">
                        Daily Activity
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {usageStats.dailyUsage.length > 0 ? (
                        <div className="h-60">
                          <Bar
                            data={{
                              labels: usageStats.dailyUsage.map((day) =>
                                formatDate2(new Date(day.date), "MMM dd")
                              ),
                              datasets: [
                                {
                                  label: "Uses",
                                  data: usageStats.dailyUsage.map(
                                    (day) => day.count
                                  ),
                                  backgroundColor: "rgba(99, 102, 241, 0.5)",
                                  borderColor: "rgb(99, 102, 241)",
                                  borderWidth: 1,
                                },
                              ],
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              scales: {
                                y: {
                                  beginAtZero: true,
                                  ticks: {
                                    precision: 0,
                                  },
                                },
                              },
                              plugins: {
                                legend: {
                                  display: false,
                                },
                                tooltip: {
                                  callbacks: {
                                    title: (items) => {
                                      if (!items.length) return "";
                                      const index = items[0].dataIndex;
                                      return usageStats.dailyUsage[index].date;
                                    },
                                    label: (item) => {
                                      const count = item.parsed.y;
                                      return `${count} use${
                                        count !== 1 ? "s" : ""
                                      }`;
                                    },
                                  },
                                },
                              },
                            }}
                          />
                        </div>
                      ) : (
                        <div className="h-60 bg-muted/10 rounded-md flex items-center justify-center">
                          <div className="text-center text-muted-foreground">
                            <BarChart2 className="h-8 w-8 mx-auto mb-2" />
                            <p>No usage data available yet</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Collaborator Performance section - only show if collaborator exists */}
                  {selectedCollaborator && promo.collaboratorId && (
                    <div className="space-y-4">
                      <h3 className="text-lg font-medium">
                        Collaborator Performance
                      </h3>

                      <div className="border rounded-md p-4">
                        <div className="flex items-center gap-3 mb-4">
                          <Avatar className="h-10 w-10">
                            <AvatarImage
                              src={selectedCollaborator.avatarUrl}
                              alt={selectedCollaborator.name}
                            />
                            <AvatarFallback>
                              {selectedCollaborator.name.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="font-medium">
                              {selectedCollaborator.name}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-1">
                              <DollarSign className="h-3 w-3" /> Earning{" "}
                              {promo.collaboratorCommissionType === "percentage"
                                ? `${promo.collaboratorCommissionValue}% of each discount`
                                : `₹${promo.collaboratorCommissionValue} per use`}
                            </div>
                          </div>
                        </div>

                        <div className="mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              navigate(
                                `/admin/collaborator/${selectedCollaborator.id}`
                              )
                            }
                          >
                            View Full Earnings Dashboard
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default PromotionForm;
